// client/src/context/TenantContext.js - STEP 1: Add back smart refresh with safeguards
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());

  // 🚨 SAFEGUARDS: Prevent infinite loops
  const refreshTimeoutRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const isRefreshingRef = useRef(false);
  
  // Minimum 10 seconds between refreshes
  const MIN_REFRESH_INTERVAL = 10000;

  // 🔧 SAFE: Get HIRS settings array
  const getHirsSettings = useCallback(() => {
    const settings = tenantSettings || window.tenantSettings;
    if (settings && settings.hirsSettings && Array.isArray(settings.hirsSettings)) {
      return settings.hirsSettings;
    }
    return [];
  }, [tenantSettings]);

  // 🚨 ENHANCED: Smart refresh with loop prevention
  const refreshTenantSettings = useCallback(async (force = false) => {
    // Prevent concurrent refreshes
    if (isRefreshingRef.current) {
      console.log('🚫 [TenantContext] Refresh already in progress, skipping');
      return;
    }

    // Check minimum time interval
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTimeRef.current;
    
    if (!force && timeSinceLastFetch < MIN_REFRESH_INTERVAL) {
      console.log(`🕒 [TenantContext] Too soon to refresh (${timeSinceLastFetch}ms < ${MIN_REFRESH_INTERVAL}ms)`);
      return;
    }

    if (!currentTenant?._id) {
      console.log('🚫 [TenantContext] No tenant ID, skipping refresh');
      return;
    }
    
    try {
      isRefreshingRef.current = true;
      lastFetchTimeRef.current = now;
      
      console.log('🔄 [TenantContext] Smart refresh starting...', { force, timeSinceLastFetch });
      
      // Smart cache busting - only if forced or data is older than 5 minutes
      const shouldBustCache = force || (timeSinceLastFetch > 300000); // 5 minutes
      const url = shouldBustCache ? 
        `/api/tenants/${currentTenant._id}/public?t=${Math.floor(now / 60000)}` : // Cache per minute
        `/api/tenants/${currentTenant._id}/public`;
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        console.log('✅ [TenantContext] Settings refreshed successfully');
        
        const safeTenantSettings = {
          ...data.data,
          hirsSettings: Array.isArray(data.data.hirsSettings) ? data.data.hirsSettings : []
        };
        
        // Only update state if data actually changed
        const currentDataHash = JSON.stringify(tenantSettings);
        const newDataHash = JSON.stringify(safeTenantSettings);
        
        if (currentDataHash !== newDataHash || force) {
          console.log('📝 [TenantContext] Data changed, updating state');
          setTenantSettings(safeTenantSettings);
          setLastRefresh(now);
          
          // Update global fallback
          window.tenantSettings = safeTenantSettings;
          window.currentTenant = currentTenant;
        } else {
          console.log('🔄 [TenantContext] Data unchanged, skipping state update');
        }
      } else {
        console.warn('⚠️ [TenantContext] Invalid response format:', data);
      }
    } catch (error) {
      console.error('❌ [TenantContext] Error refreshing:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [currentTenant?._id, tenantSettings]); // Include tenantSettings for change detection

  // Load tenant from storage on mount
  useEffect(() => {
    const loadTenant = () => {
      try {
        const storedTenant = localStorage.getItem('tenant') || sessionStorage.getItem('tenant');
        if (storedTenant) {
          const tenant = JSON.parse(storedTenant);
          console.log('📱 [TenantContext] Loaded tenant from storage:', tenant.name);
          setCurrentTenant(tenant);
        } else {
          console.log('📱 [TenantContext] No tenant in storage');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('❌ [TenantContext] Error loading tenant:', error);
        setIsLoading(false);
      }
    };

    loadTenant();
  }, []);

  // 🚨 SAFE: Initial fetch only when tenant changes
  useEffect(() => {
    if (currentTenant?._id) {
      console.log('🔍 [TenantContext] New tenant, fetching settings:', currentTenant.name);
      fetchTenantSettings(currentTenant._id);
    } else {
      setIsLoading(false);
      setTenantSettings(null);
    }
  }, [currentTenant?._id]); // Only depend on tenant ID

  const fetchTenantSettings = async (tenantId) => {
    try {
      setIsLoading(true);
      isRefreshingRef.current = true;
      
      const now = Date.now();
      lastFetchTimeRef.current = now;
      
      console.log('🔍 [TenantContext] Initial fetch for tenant:', tenantId);
      
      const response = await fetch(`/api/tenants/${tenantId}/public`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        console.log('✅ [TenantContext] Initial settings loaded successfully');
        
        const safeTenantSettings = {
          ...data.data,
          hirsSettings: Array.isArray(data.data.hirsSettings) ? data.data.hirsSettings : []
        };
        
        setTenantSettings(safeTenantSettings);
        setLastRefresh(now);
        
        // Update global fallback
        window.tenantSettings = safeTenantSettings;
        window.currentTenant = currentTenant;
      } else {
        console.warn('⚠️ [TenantContext] Invalid initial response:', data);
      }
    } catch (error) {
      console.error('❌ [TenantContext] Error fetching initial settings:', error);
    } finally {
      setIsLoading(false);
      isRefreshingRef.current = false;
    }
  };

  const updateTenant = (tenant) => {
    console.log('🔄 [TenantContext] Updating tenant:', tenant?.name || 'null');
    setCurrentTenant(tenant);
    if (tenant) {
      fetchTenantSettings(tenant._id);
    } else {
      setTenantSettings(null);
    }
  };

  // 🔧 ENHANCED: Feature check with better logging
  const isFeatureEnabled = useCallback((featureName) => {
    try {
      const settings = tenantSettings || window.tenantSettings;
      
      if (!settings) {
        console.log(`🔧 [Feature] ${featureName}: No settings, defaulting to enabled`);
        return true;
      }
      
      const hirsArray = getHirsSettings();
      if (hirsArray.length === 0) {
        console.log(`🔧 [Feature] ${featureName}: No HIRS settings, defaulting to enabled`);
        return true;
      }

      // Find feature by name (case-insensitive)
      let feature = null;
      
      if (typeof featureName === 'string') {
        feature = hirsArray.find(hirs => 
          hirs && hirs.name && hirs.name.toLowerCase() === featureName.toLowerCase()
        );
      }
      
      // Find by numeric ID if string search failed
      if (!feature && (typeof featureName === 'number' || !isNaN(parseInt(featureName)))) {
        const numericId = typeof featureName === 'number' ? featureName : parseInt(featureName);
        feature = hirsArray.find(hirs => hirs && hirs.id === numericId);
      }
      
      // Exact match fallback
      if (!feature) {
        feature = hirsArray.find(hirs => 
          hirs && (hirs.name === featureName || hirs.id === featureName)
        );
      }
      
      const isEnabled = feature ? Boolean(feature.isActive) : true;
      console.log(`🔧 [Feature] ${featureName}: ${isEnabled ? 'ENABLED' : 'DISABLED'}`);
      
      return isEnabled;
      
    } catch (error) {
      console.error('❌ [Feature] Error checking feature:', featureName, error);
      return true; // Default to enabled on error
    }
  }, [tenantSettings, getHirsSettings]);

  // 🔧 ENHANCED: Theme styles with better fallbacks
  const getThemeStyles = useCallback(() => {
    const settings = tenantSettings || window.tenantSettings;
    
    const defaultTheme = {
      primaryColor: '#4CAF50',
      secondaryColor: '#2196F3',
      logo: null,
      darkLogo: null,
      systemLogo: { light: null, dark: null },
      favicon: { light: null, dark: null }
    };
    
    if (!settings) {
      console.log('🎨 [Theme] No settings, using defaults');
      return defaultTheme;
    }

    const theme = {
      primaryColor: settings.primaryColor || defaultTheme.primaryColor,
      secondaryColor: settings.secondaryColor || defaultTheme.secondaryColor,
      logo: settings.systemLogo?.light || null,
      darkLogo: settings.systemLogo?.dark || null,
      systemLogo: {
        light: settings.systemLogo?.light || null,
        dark: settings.systemLogo?.dark || null
      },
      favicon: {
        light: settings.favicon?.light || null,
        dark: settings.favicon?.dark || null
      }
    };
    
    console.log('🎨 [Theme] Loaded theme:', { 
      hasLogo: !!theme.logo, 
      hasDarkLogo: !!theme.darkLogo,
      primaryColor: theme.primaryColor 
    });
    
    return theme;
  }, [tenantSettings]);

  // 🚨 SAFE: Event listener for manual updates only
  useEffect(() => {
    const handleSettingsUpdate = (event) => {
      console.log('🔔 [TenantContext] Manual settings update event received');
      
      // Clear any existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      
      // Debounced refresh with force=true
      refreshTimeoutRef.current = setTimeout(() => {
        console.log('🔄 [TenantContext] Executing delayed refresh...');
        refreshTenantSettings(true);
      }, 3000); // 3 second delay to allow admin changes to propagate
    };

    window.addEventListener('tenantSettingsUpdated', handleSettingsUpdate);
    
    return () => {
      window.removeEventListener('tenantSettingsUpdated', handleSettingsUpdate);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [refreshTenantSettings]);

  // 🚫 STILL REMOVED: All automatic refresh logic
  // ❌ No periodic intervals
  // ❌ No focus event listeners
  // ❌ No storage event listeners
  // ❌ No BroadcastChannel

  const value = {
    currentTenant,
    tenantSettings,
    isLoading,
    lastRefresh,
    updateTenant,
    isFeatureEnabled,
    getThemeStyles,
    getHirsSettings,
    refreshTenantSettings,
    platformName: (tenantSettings || window.tenantSettings)?.platformName || 'NEUROLEX',
    platformDescription: (tenantSettings || window.tenantSettings)?.platformDescription || 'Mental wellness platform'
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};